import { Box, Grid, IconButton, TextField } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import type { ChildCustomerOverview, CustomerRelationType } from '@vy/types';
import { CUSTOMER_RELATION_TYPES } from '@vy/types';
import { fromNullToUndefined, values } from '@vy/utils';
import { memo } from 'react';
import { BaseSelect } from '../../../../components/design/selects/BaseSelect';

type ViewCustomerChildrenProps = {
  customerName: string;
  customerChild: ChildCustomerOverview;
  onDeleteChild: (childId: string) => void;
  onRelationTypeChange: (childId: string, newRelationType: CustomerRelationType) => void;
  relationType: CustomerRelationType;
};

export const ViewCustomerChildren = memo(({
  customerChild,
  onDeleteChild,
  customerName,
  onRelationTypeChange,
  relationType,
}: ViewCustomerChildrenProps) => {
  const getRelationTypeLabel = (relationType: CustomerRelationType) => {
    const labelMap: Record<CustomerRelationType, string> = {
      [CUSTOMER_RELATION_TYPES.accumulateUsage]: `Aggregate usage to ${customerName}`,
      [CUSTOMER_RELATION_TYPES.accumulateCharges]: `Aggregate charges to ${customerName}`,
      [CUSTOMER_RELATION_TYPES.erpInvoiceSyncOnBehalfParent]: `Invoice sync to ${customerName} (ERP)`,
    };

    return labelMap[relationType] || `Unknown relation to ${customerName}`;
  };

  return (
    <Grid container spacing={2} sx={{
      alignItems: "center"
    }}>
      <Grid size="grow">
        <TextField label="Name" fullWidth disabled value={customerChild.name} />
      </Grid>
      <Grid size="grow">
        <TextField
          label="External ID"
          fullWidth
          disabled
          value={fromNullToUndefined(customerChild.externalId)}
        />
      </Grid>
      <Grid size="grow">
        <BaseSelect
          label="Invoice preferences"
          value={relationType}
          values={values(CUSTOMER_RELATION_TYPES)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onRelationTypeChange(customerChild.id, e.target.value as CustomerRelationType)
          }
          getLabelOption={(option: string) => getRelationTypeLabel(option as CustomerRelationType)}
          withNone={false}
          fullWidth
          slotProps={{
            select: {
              renderValue: (selected: unknown) => (
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={getRelationTypeLabel(selected as CustomerRelationType)}
                >
                  {getRelationTypeLabel(selected as CustomerRelationType)}
                </div>
              ),
            },
          }}
        />
      </Grid>
      <Grid size="auto" sx={{
        height: "100%"
      }}>
        <Box
        sx={{
          mt: 'auto',
          pt: 3,
        }}>
          <IconButton onClick={() => onDeleteChild(customerChild.id)}>
            <VyIcon icon="Truncate" />
          </IconButton>
        </Box>
      </Grid>
    </Grid>
  );
});
